import { useContent } from '../../contexts/ContentContext'
import SectionLabel from '../ui/SectionLabel'
import CtaButton from '../ui/CtaButton'
import LazyImage from '../ui/LazyImage'
import {
  resolveSectionTone,
  sectionTextClasses,
  sectionOverlayClass,
} from '../../utils/sectionTone'

/**
 * FinalCTASection — full-bleed background + round portrait + copy + CTA.
 * Content: finalCTA | ctaImage
 * Contrast via sectionTone (image/navy → onDark by default).
 */
export default function FinalCTASection() {
  const { finalCTA, ctaImage } = useContent()
  const data = finalCTA || ctaImage
  if (!data) return null

  const bg = data.backgroundUrl || data.imageUrl
  const portrait = data.portraitUrl || data.imageUrl
  const tone = resolveSectionTone({
    ...data,
    surface: data.surface || (bg ? 'image' : 'blue'),
    backgroundUrl: bg,
  })
  const t = sectionTextClasses(tone)

  return (
    <section
      id={data.id || 'final-cta'}
      data-section="FinalCTASection"
      data-testid="final-cta-section"
      data-tone={tone}
      className="relative min-h-[100svh] snap-section flex items-center overflow-hidden"
    >
      {bg ? (
        <>
          <div
            className="absolute inset-0 bg-cover bg-center"
            style={{ backgroundImage: `url(${bg})` }}
            aria-hidden="true"
          />
          <div
            className={`absolute inset-0 ${
              data.overlay ||
              (t.onDark ? 'bg-color-primary/70' : sectionOverlayClass(tone))
            }`}
            aria-hidden="true"
          />
        </>
      ) : (
        <div
          className={`absolute inset-0 ${t.onDark ? 'bg-color-primary' : 'bg-color-bg-light'}`}
          aria-hidden="true"
        />
      )}

      <div className="relative z-10 w-full max-w-content mx-auto px-6 md:px-10 lg:px-16 py-16 md:py-24">
        <div className="grid sm:grid-cols-[240px_1fr] md:grid-cols-[288px_1fr] card-grid-gap-sm md:card-grid-gap-lg items-center">
          {portrait ? (
            <div className="flex justify-center md:justify-start">
              <div
                className={`w-40 h-40 sm:w-48 sm:h-48 md:w-64 md:h-64 lg:w-72 lg:h-72 rounded-full overflow-hidden flex-shrink-0 border-2 shadow-lg ${
                  t.onDark ? 'border-primary-on-dark/80' : 'border-color-primary/40'
                }`}
              >
                <LazyImage
                  src={portrait}
                  alt={data.imageAlt || ''}
                  className="w-full h-full object-cover object-top scale-[1.03]"
                />
              </div>
            </div>
          ) : null}

          <div className="text-center sm:text-left">
            {data.label ? <SectionLabel text={data.label} light={t.onDark} /> : null}

            {data.headline ? (
              <h2 className={`${t.display} leading-tight heading-body-spacing`}>
                {data.headline}
              </h2>
            ) : null}

            {data.subline ? (
              <p className={`${t.body} leading-relaxed`}>{data.subline}</p>
            ) : null}

            {data.cta ? (
              <div className="margin-top-md flex justify-center sm:justify-start">
                <CtaButton label={data.cta} variant="primary" onDark={t.onDark} className="py-4 px-12" />
              </div>
            ) : null}
          </div>
        </div>
      </div>
    </section>
  )
}

/** @deprecated alias — regression/mentorship maps may still reference CtaImageSection */
export { FinalCTASection as CtaImageSection }
